import React from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { graphql } from 'gatsby';
import { GatsbyImage } from 'gatsby-plugin-image';
import AudioPlayer, { RHAP_UI } from 'react-h5-audio-player';
import 'react-h5-audio-player/lib/styles.css';

import { useMusicLibrarySectionData } from './hooks';

import PlayIcon from '../assets/playIcon.inline.svg';
import PauseIcon from '../assets/pauseIcon.inline.svg';

import { Normalize } from 'utils/types';
import { RichText } from 'components/Prismic';

export type TMusicLibrarySection = Normalize<Queries.HomeContentMusicLibraryDataFragment>;

const Container = styled.div`
  ${({ theme: { colors } }) => css`
    display: grid;
    background-color: ${colors.backgroundDark};
  `}
`;

const Title = styled(RichText)`
  ${({ theme: { colors, mq, spacings, tp } }) => css`
    display: flex;
    flex-direction: column;

    & > h2 {
      padding: 5.25rem 7.75rem;
      line-height: 0.83;
      letter-spacing: -0.05em;
      color: #6c6c6c;
      font-size: 9.75rem;

      ${tp.useFont(fonts => fonts.Switzer.lightest)};

      & > strong {
        line-height: 0.77;
        letter-spacing: -0.11em;
        color: ${colors.primary};
        font-size: 18rem;

        ${tp.useFont(fonts => fonts.Switzer.regular)};
      }
    }

    ${mq.md} {
      & > h2 {
        padding: 1.25rem ${spacings.padding} 2rem;
        line-height: 1.12;
        font-size: 3.75rem;

        & > strong {
          line-height: 0.77;
          font-size: 6.75rem;
        }
      }
    }
  `}
`;

const CategoriesContainer = styled.div`
  ${({ theme: { mq } }) => css`
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    grid-auto-rows: 39.3vw;

    ${mq.md} {
      grid-template-rows: 30rem;
      grid-template-columns: initial;
      grid-auto-rows: initial;
      grid-auto-columns: 15rem;
      grid-auto-flow: column;
      padding-bottom: 6.25rem;
      width: 100%;
      overflow-x: auto;

      &::-webkit-scrollbar {
        display: none;
      }
    }
  `}
`;

const Category = styled.div`
  ${({ theme: { mq } }) => css`
    display: grid;
    overflow: hidden;

    ${mq.md} {
      position: relative;
      overflow: initial;
    }
  `}
`;

const CategoryImage = styled(GatsbyImage)`
  ${({ theme: { mq } }) => css`
    grid-row: 1 / span 1;
    grid-column: 1 / -1;
    transition: transform 8s;

    &:hover {
      transform: scale(2);
      transition-duration: 4s;
    }

    ${mq.md} {
      &:hover {
        transform: initial;
      }
    }
  `}
`;

const CategoryContent = styled.div`
  display: flex;
  flex-direction: column;
  grid-row: 1 / span 1;
  grid-column: 1 / -1;
  row-gap: 2rem;
  align-items: center;
  align-self: center;
  justify-self: center;
  z-index: 1;
`;

const CategoryTitle = styled(RichText)`
  ${({ theme: { colors, mq, tp } }) => css`
    display: flex;
    flex-direction: column;

    & > h6 {
      text-align: center;
      line-height: 0.714;
      color: ${colors.white}B3;
      font-size: 3.75rem;

      ${tp.useFont(fonts => fonts['Nunito Sans'].veryBold)};
    }

    ${mq.md} {
      position: absolute;
      bottom: -1.75rem;
      transform: translateY(100%);
      width: 85%;

      & > h6 {
        line-height: 1.5;
        color: ${colors.white}99;
        font-size: 1.5rem;
      }
    }
  `}
`;

const PlayButton = styled.div`
  ${({ theme: { colors, mq } }) => css`
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: ${colors.white}0D;
    cursor: pointer;
    width: 12.5rem;
    aspect-ratio: 1 / 1;
    backdrop-filter: blur(1.1875rem);

    ${mq.md} {
      width: 4rem;
    }
  `}
`;

const StyledPlayIcon = styled(PlayIcon)`
  ${({ theme: { mq } }) => css`
    margin-left: 0.875rem;
    height: 5.5rem;

    ${mq.md} {
      margin-left: 0.5rem;
      height: 2rem;
    }
  `}
`;

const StyledPauseIcon = styled(PauseIcon)`
  ${({ theme: { mq } }) => css`
    ${mq.md} {
      height: 2rem;
    }
  `}
`;

const StyledAudioPlayer = styled(AudioPlayer)`
  &.rhap_container {
    box-shadow: initial;
    background-color: transparent;
    padding: initial;
    height: 100%;
  }

  .rhap_progress-section {
    width: 100%;
  }

  .rhap_main-controls {
    width: 100%;
    height: 100%;
  }

  .rhap_play-pause-button {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
  }

  .rhap_controls-section {
    display: none;
  }
`;

const MusicLibrarySection: React.FC<TMusicLibrarySection> = sliceData => {
  const { title, categories, getTrackPlayerRef, composeOnPlay } =
    useMusicLibrarySectionData(sliceData);

  return (
    <Container>
      <Title render={title} />
      <CategoriesContainer>
        {categories.map(({ background, title, audio }, index) => {
          const key = `${title}${audio}${index}`;

          return (
            <Category key={key}>
              <CategoryImage {...background} />
              <CategoryContent>
                <PlayButton>
                  <StyledAudioPlayer
                    src={audio}
                    ref={getTrackPlayerRef(key)}
                    autoPlay={false}
                    customProgressBarSection={[RHAP_UI.MAIN_CONTROLS]}
                    customAdditionalControls={[]}
                    customControlsSection={[]}
                    customVolumeControls={[]}
                    showJumpControls={false}
                    showDownloadProgress={false}
                    customIcons={{
                      play: <StyledPlayIcon />,
                      pause: <StyledPauseIcon />,
                    }}
                    onPlay={composeOnPlay(index)}
                  />
                </PlayButton>
                <CategoryTitle render={title} />
              </CategoryContent>
            </Category>
          );
        })}
      </CategoriesContainer>
    </Container>
  );
};

export const contentMusicLibraryFragment = graphql`
  fragment HomeContentMusicLibraryData on PrismicHomeDataContentHomemusiclibrary {
    slice_type
    primary {
      home_music_library_title {
        richText
      }
    }
    items {
      home_music_library_grid_title {
        richText
      }
      home_music_library_grid_image {
        gatsbyImageData(placeholder: BLURRED)
        alt
      }
      home_music_library_grid_audio {
        url
      }
    }
  }
`;

export default MusicLibrarySection;
